// SPDX-License-Identifier: Apache-2.0

// EmbodiedRig — Solid port of v1's first-person flight rig.
//
// The rig is a *headless* component: it returns null and only side-effects
// the camera + DOM event listeners. The host (Hephaestus's <VaultOrbit3D>)
// drives the actual render loop and calls camera.updateMatrixWorld() each
// frame; we just nudge camera.position / camera.quaternion ahead of that.
//
// Public surface is intentionally tiny — see the prop interface. The host
// owns:
//   - the canvas element (we attach pointer-lock to it via document target)
//   - the THREE.PerspectiveCamera reference
//   - the requestAnimationFrame loop (we self-drive ours; see below)
//   - the toggle that flips `enabled()` (Hermes wires it from the topbar)
//
// Why we self-drive an rAF loop: the host's render loop runs whether we're
// embodied or not, but we don't have access to its `dt`. Running our own rAF
// here keeps the controls package independent of the orbit's scheduler. When
// `enabled()` flips false we cancel the rAF and stop touching the camera.

import { onCleanup, onMount, createEffect } from "solid-js";
import * as THREE from "three";
import {
  applyMouseLook,
  applyMovement,
  isTypingTarget,
  keyToSlot,
  makeKeyState,
  resetKeyState,
} from "./embodied-controls";

export interface EmbodiedRigProps {
  /** Camera the rig drives. Must be perspective (orthographic look-around
   *  doesn't make sense). */
  camera: THREE.PerspectiveCamera;
  /** Reactive accessor — when this returns true, the rig engages. */
  enabled: () => boolean;
  /** Movement speed in units/sec. Default 5. */
  speed?: number;
  /** Mouse-look sensitivity in radians/pixel. Default 0.002. */
  mouseSensitivity?: number;
  /**
   * Element to which we attach pointer-lock. Defaults to document.body —
   * fine for v1. Hephaestus can pass a specific canvas later if needed.
   */
  target?: () => HTMLElement | null;
}

export function EmbodiedRig(props: EmbodiedRigProps): null {
  const speed = () => props.speed ?? 5;
  const sensitivity = () => props.mouseSensitivity ?? 0.002;

  const keys = makeKeyState();
  const scratch = {
    fwd: new THREE.Vector3(),
    right: new THREE.Vector3(),
  };

  let rafId: number | null = null;
  let lastTime = 0;
  let pointerLocked = false;
  // Cleanup callbacks registered while engaged; replayed when we disengage.
  let teardown: Array<() => void> = [];

  function getTarget(): HTMLElement {
    return props.target?.() ?? document.body;
  }

  function frame(now: number) {
    if (!props.enabled()) {
      rafId = null;
      return;
    }
    const dt = lastTime === 0 ? 0 : Math.min((now - lastTime) / 1000, 0.1);
    lastTime = now;
    if (pointerLocked && dt > 0) {
      applyMovement(props.camera, dt, keys, { speed: speed(), scratch });
    }
    rafId = requestAnimationFrame(frame);
  }

  function engage() {
    if (teardown.length > 0) return; // already engaged

    const target = getTarget();

    // ── pointerdown-to-lock ───────────────────────────────────────
    // Apollo 4 — listen for pointerdown rather than click so we don't depend
    // on the synthesized click event firing. The host canvas calls
    // preventDefault() on pointerdown when a node is hovered, which can
    // suppress the click roundtrip in some browsers; pointerdown itself is
    // a valid user-activation source for requestPointerLock and it always
    // fires first. Bail when already locked — re-requesting is harmless but
    // generates console noise.
    const onPointerDown = () => {
      if (!props.enabled()) return;
      if (document.pointerLockElement === target) return;
      try {
        target.requestPointerLock();
      } catch {
        /* swallow — feature-detect failures are non-fatal */
      }
    };
    target.addEventListener("pointerdown", onPointerDown);
    teardown.push(() => target.removeEventListener("pointerdown", onPointerDown));

    // ── pointerlockchange — sync our flag and stop motion when released
    const onLockChange = () => {
      pointerLocked = document.pointerLockElement === target;
      if (!pointerLocked) resetKeyState(keys);
    };
    document.addEventListener("pointerlockchange", onLockChange);
    teardown.push(() =>
      document.removeEventListener("pointerlockchange", onLockChange),
    );

    // ── mousemove — only meaningful while locked
    const onMouseMove = (e: MouseEvent) => {
      if (!pointerLocked) return;
      applyMouseLook(props.camera, e.movementX, e.movementY, sensitivity());
    };
    document.addEventListener("mousemove", onMouseMove);
    teardown.push(() =>
      document.removeEventListener("mousemove", onMouseMove),
    );

    // ── keydown / keyup
    const onKeyDown = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target)) return;
      const slot = keyToSlot(e.key);
      if (slot === null) return;
      e.preventDefault();
      keys[slot] = true;
    };
    const onKeyUp = (e: KeyboardEvent) => {
      const slot = keyToSlot(e.key);
      if (slot === null) return;
      keys[slot] = false;
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    teardown.push(() => window.removeEventListener("keydown", onKeyDown));
    teardown.push(() => window.removeEventListener("keyup", onKeyUp));

    // ── start the rAF
    lastTime = 0;
    if (rafId === null) rafId = requestAnimationFrame(frame);
  }

  function disengage() {
    if (teardown.length === 0 && rafId === null && !pointerLocked) return;
    for (const fn of teardown) {
      try {
        fn();
      } catch {
        /* swallow — best-effort cleanup */
      }
    }
    teardown = [];
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    resetKeyState(keys);
    if (pointerLocked && document.exitPointerLock) {
      try {
        document.exitPointerLock();
      } catch {
        /* swallow */
      }
    }
    pointerLocked = false;
  }

  onMount(() => {
    if (props.enabled()) engage();
  });

  // React to enabled() flipping at runtime. createEffect tracks the accessor.
  createEffect(() => {
    if (props.enabled()) engage();
    else disengage();
  });

  onCleanup(() => disengage());

  return null;
}
